(() => {
  const DESKTOP_QUERY = '(min-width: 1100px)';
  const DEFAULT_AVAILABILITY_FILTER_ID = '851678';
  const FILTER_ORDER = {
    availability: 10,
    price: 20,
    brand: 30,
    category: 40,
    condition: 50,
    'clothing-size': 60,
    'shoe-size': 70,
  };
  const PRODUCT_CARD_SELECTOR =
    '#gf-products .card-wrapper.product-card-wrapper';
  const COLLECTION_SEARCH_SELECTOR = '#gf-controls-search-input';
  const SELECTED_FILTER_CONTROL_SELECTOR = [
    '.gf-block-content button.checked',
    '.gf-block-content button.selected',
    '.gf-block-content button[aria-checked="true"]',
    '.gf-block-content a.checked',
    '.gf-block-content a.selected',
    '.gf-block-content a[aria-checked="true"]',
    '.gf-block-content input:checked',
    '.gf-block-content [role="checkbox"][aria-checked="true"]',
    '.gf-block-content [role="radio"][aria-checked="true"]',
    '.gf-block-content li.checked button',
    '.gf-block-content li.checked a',
    '.gf-block-content li.selected button',
    '.gf-block-content li.selected a',
  ].join(', ');
  let treePlaceholder = null;
  let resizeFrame = null;
  let renderFrame = null;
  let compatibilityObserver = null;
  let drawerStateObserver = null;
  let lastPointerResetAt = 0;
  let defaultAvailabilityAttempted = false;
  let defaultAvailabilityRedirected = false;
  const productRequests = new Map();

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const getFilterKind = (label) => {
    if (/availability|in stock|stock status/.test(label)) return 'availability';
    if (/clothing size|apparel size/.test(label)) return 'clothing-size';
    if (/shoe size|footwear size/.test(label)) return 'shoe-size';
    if (/price/.test(label)) return 'price';
    if (/brand|vendor|designer/.test(label)) return 'brand';
    if (/category|product type/.test(label)) return 'category';
    if (/condition/.test(label)) return 'condition';
    return '';
  };

  const getGridItem = (card) => {
    let item = card;

    while (item?.parentElement && item.parentElement.id !== 'gf-products') {
      item = item.parentElement;
    }

    return item?.parentElement?.id === 'gf-products' ? item : card;
  };

  const getSelectedFilterCount = (block) => {
    const selectedControls = new Set(block.querySelectorAll(SELECTED_FILTER_CONTROL_SELECTOR));

    if (selectedControls.size) return selectedControls.size;

    return block.querySelector('.gf-block-title .gf-clear[data-fid]') ? 1 : 0;
  };

  const getAvailabilityControl = (block) => {
    const controls = Array.from(
      block.querySelectorAll(
        '.gf-block-content button[data-fvalue], .gf-block-content button[aria-checked], .gf-block-content input[type="checkbox"], .gf-block-content input[type="radio"], .gf-block-content a[data-fvalue], .gf-block-content [role="checkbox"], .gf-block-content [role="radio"]'
      )
    ).filter((control) => !control.classList.contains('ds-gf-stock-toggle'));

    return (
      controls.find((control) => {
        const label = normalize(
          control.getAttribute('aria-label') ||
            control.getAttribute('title') ||
            control.textContent
        );
        return (
          /(^| )(in stock|available|true|yes)( |$)/.test(label) &&
          !/(^| )(unavailable|out of stock|sold out|false|no)( |$)/.test(label)
        );
      }) ||
      controls.find((control) => {
        const label = normalize(
          control.getAttribute('aria-label') ||
            control.getAttribute('title') ||
            control.textContent
        );
        return !/(^| )(unavailable|out of stock|sold out|false|no)( |$)/.test(label);
      }) ||
      controls[0]
    );
  };

  const syncAvailabilityToggle = (block, selectedCount) => {
    let toggle = block.querySelector('.ds-gf-stock-toggle');

    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'ds-gf-stock-toggle';
      toggle.setAttribute('aria-label', 'In stock');
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const availabilityControl = getAvailabilityControl(block);
        if (!availabilityControl) return;

        const nextState = toggle.getAttribute('aria-pressed') !== 'true';
        toggle.setAttribute('aria-pressed', String(nextState));
        availabilityControl.click();
      });
      block.appendChild(toggle);
    }

    const availabilityControl = getAvailabilityControl(block);
    const isSelected =
      selectedCount > 0 ||
      availabilityControl?.matches('.checked, [aria-checked="true"], input:checked');

    toggle.setAttribute('aria-pressed', String(Boolean(isSelected)));
    toggle.setAttribute('aria-disabled', String(!availabilityControl));
  };

  const applyDefaultAvailability = (block, selectedCount) => {
    const url = new URL(window.location.href);
    const defaultParam = `filter.${DEFAULT_AVAILABILITY_FILTER_ID}`;

    if (
      url.searchParams.get(defaultParam) !== '1' ||
      selectedCount > 0 ||
      defaultAvailabilityAttempted ||
      defaultAvailabilityRedirected
    ) {
      return;
    }

    const actualFilterId = block.dataset.filterId;

    /* If the filter is recreated in Globo and receives a new ID, migrate the
       default URL to that ID instead of leaving a visually inactive toggle. */
    if (actualFilterId && actualFilterId !== DEFAULT_AVAILABILITY_FILTER_ID) {
      defaultAvailabilityRedirected = true;
      url.searchParams.delete(defaultParam);
      url.searchParams.set(`filter.${actualFilterId}`, '1');
      window.location.replace(url.href);
      return;
    }

    const availabilityControl = getAvailabilityControl(block);
    const isSelected = availabilityControl?.matches(
      '.checked, [aria-checked="true"], input:checked'
    );

    if (!availabilityControl || isSelected) return;

    /* Some Globo renders build the filter tree before hydrating selections.
       Activate its real control once so results and UI cannot drift apart. */
    defaultAvailabilityAttempted = true;
    availabilityControl.click();
  };

  const labelFilterBlocks = () => {
    const tree = document.getElementById('gf-tree');
    if (!tree) return;

    let totalSelectedFilters = 0;

    tree.querySelectorAll('.gf-option-block').forEach((block) => {
      const titleText = block.querySelector('.gf-block-title .h3 span, .gf-block-title h3 span');
      const titleControl = block.querySelector('.gf-block-title .h3, .gf-block-title h3');

      if (!block.dataset.dsCollapseInitialized) {
        block.classList.add('is-collapsed');
        block.dataset.dsCollapseInitialized = 'true';
      }

      if (titleText && !titleText.dataset.dsOriginalLabel) {
        titleText.dataset.dsOriginalLabel = titleText.textContent.trim();
      }

      const originalLabel = titleText?.dataset.dsOriginalLabel || titleText?.textContent || '';
      const kind = getFilterKind(normalize(originalLabel));
      const selectedCount = getSelectedFilterCount(block);

      totalSelectedFilters += selectedCount;

      block.style.setProperty('--ds-gf-order', String(FILTER_ORDER[kind] || 100));
      if (titleText) {
        titleText.textContent = `${originalLabel}${selectedCount ? ` (${selectedCount})` : ''}`;
      }
      titleControl?.setAttribute(
        'aria-label',
        `${originalLabel}${selectedCount ? `, ${selectedCount} selected` : ''}`
      );

      if (!kind) return;

      block.dataset.dsFilterKind = kind;
      if (kind !== 'availability') return;

      let inStockChoice = null;
      const availabilityChoices = Array.from(
        block.querySelectorAll('.gf-option-box > li')
      );

      availabilityChoices.forEach((choice) => {
        const choiceLabel = normalize(choice.textContent);
        const isInStock =
          /(^| )in stock( |$)|available/.test(choiceLabel) &&
          !/unavailable|out of stock/.test(choiceLabel);

        choice.classList.toggle('ds-gf-stock-choice', isInStock);
        if (isInStock) inStockChoice = choice;
      });

      if (!inStockChoice) {
        inStockChoice =
          availabilityChoices.find((choice) => {
            const choiceLabel = normalize(choice.textContent);
            return !/(^| )(unavailable|out of stock|sold out|false|no)( |$)/.test(
              choiceLabel
            );
          }) || availabilityChoices[0];

        inStockChoice?.classList.add('ds-gf-stock-choice');
      }

      syncAvailabilityToggle(block, selectedCount);
      applyDefaultAvailability(block, selectedCount);

      if (titleText) titleText.textContent = 'In stock';
      titleControl?.setAttribute(
        'aria-label',
        `In stock${selectedCount ? ', selected' : ''}`
      );

      const stockControl =
        inStockChoice?.querySelector('button, a, input') || getAvailabilityControl(block);
      stockControl?.setAttribute('aria-label', 'In stock');
    });

    tree.dataset.dsActiveFilterCount = String(totalSelectedFilters);
  };

  const runExistingReset = (reset) => {
    const tree = reset?.closest('#gf-tree') || document.getElementById('gf-tree');
    const controls = document.getElementById('gf-controls-container');
    const clearAll =
      tree?.querySelector('.globo-selected-items-wrapper .clear-refinements') ||
      controls?.querySelector('.globo-selected-items-wrapper .clear-refinements') ||
      document.querySelector('.globo-selected-items-wrapper .clear-refinements');

    if (clearAll) {
      clearAll.click();
      return;
    }

    /* Globo only renders Clear All for multiple selections. A single filter
       uses its selected-item link (removeFilterValue/removeFilter) instead. */
    const selectedItemReset =
      tree?.querySelector(
        '.globo-selected-items-wrapper .selected-item.gf-option-label > a:not(.clear-refinements)'
      ) ||
      controls?.querySelector(
        '.globo-selected-items-wrapper .selected-item.gf-option-label > a:not(.clear-refinements)'
      ) ||
      document.querySelector(
        '.globo-selected-items-wrapper .selected-item.gf-option-label > a:not(.clear-refinements)'
      );

    if (selectedItemReset) {
      selectedItemReset.click();
      return;
    }

    const blockReset = tree?.querySelector(
      '.gf-option-block .gf-block-title .gf-clear[data-fid], .gf-option-block .gf-block-title .gf-clear'
    );

    if (blockReset) {
      blockReset.click();
      return;
    }

    const selectedOption = tree?.querySelector(SELECTED_FILTER_CONTROL_SELECTOR);
    selectedOption?.click();
  };

  const getResetFromEvent = (event) =>
    event.target instanceof Element ? event.target.closest('.ds-gf-reset') : null;

  const handleDelegatedResetPointerUp = (event) => {
    const reset = getResetFromEvent(event);

    if (
      !reset ||
      !window.matchMedia(DESKTOP_QUERY).matches ||
      !event.isPrimary ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    lastPointerResetAt = performance.now();
    runExistingReset(reset);
  };

  const handleDelegatedResetClick = (event) => {
    const reset = getResetFromEvent(event);

    if (!reset) return;

    if (performance.now() - lastPointerResetAt < 500) {
      event.preventDefault();
      return;
    }

    runExistingReset(reset);
  };

  const positionResetControl = () => {
    const tree = document.getElementById('gf-tree');
    const contents = tree?.querySelector('.gf-filter-contents');
    if (!tree || !contents) return;

    const activeFilterCount = Number(tree.dataset.dsActiveFilterCount || 0);
    let reset = tree.querySelector('.ds-gf-reset:not(.gf-clear-all)');

    if (!reset && activeFilterCount > 0) {
      reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'ds-gf-reset';
    }

    if (!reset) return;

    reset.classList.add('ds-gf-reset');
    reset.classList.remove('ds-gf-selected-summary');
    reset.style.removeProperty('display');
    reset.removeAttribute('aria-hidden');
    reset.textContent = 'Reset';
    reset.setAttribute('aria-label', 'Reset filters');
    reset.hidden = activeFilterCount === 0;

    if (!reset.matches('button, a, input')) {
      reset.setAttribute('role', 'button');
      reset.setAttribute('tabindex', '0');

      if (reset.dataset.dsKeyboardReady !== 'true') {
        reset.dataset.dsKeyboardReady = 'true';
        reset.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          reset.click();
        });
      }
    }

    contents.appendChild(reset);
  };

  const hideSelectedFilterSummary = () => {
    const tree = document.getElementById('gf-tree');
    const contents = tree?.querySelector('.gf-filter-contents');
    if (!tree || !contents) return;

    const selectedLabelPattern =
      /^(?:availability|in stock|price|brand|category|condition|clothing size|shoe size)\s*:/i;
    const duplicateHeadingPattern = /^(?:filter by|clear all)$/i;
    const walker = document.createTreeWalker(tree, NodeFilter.SHOW_TEXT);
    const matches = [];
    let textNode = walker.nextNode();

    while (textNode) {
      const text = textNode.nodeValue.trim();
      const element = textNode.parentElement;
      const isProtected = element?.closest(
        '.gf-option-block, .gf-filter-header, .gf-filter-footer, .ds-gf-reset'
      );

      if (
        element &&
        !isProtected &&
        (selectedLabelPattern.test(text) || duplicateHeadingPattern.test(text))
      ) {
        matches.push(element);
      }

      textNode = walker.nextNode();
    }

    tree
      .querySelectorAll('.gf-clear-all:not(.ds-gf-reset)')
      .forEach((element) => matches.push(element));

    matches.forEach((element) => {
      let current = element;

      while (
        current &&
        current !== contents &&
        current !== tree &&
        !current.matches('.gf-filter-contents, .gf-filter-contents-inner') &&
        !current.matches('.gf-filter-header, .gf-filter-footer, .gf-option-block')
      ) {
        current.classList.add('ds-gf-selected-summary');
        current.hidden = true;
        current.setAttribute('aria-hidden', 'true');
        current.style.setProperty('display', 'none', 'important');

        const parent = current.parentElement;
        if (!parent || parent.querySelector('.gf-option-block')) break;
        current = parent;
      }
    });
  };

  const labelFilterFooter = () => {
    document.querySelectorAll('#gf-tree .gf-filter-footer button').forEach((button) => {
      button.setAttribute('aria-label', 'View products');
    });
  };

  const enhanceEmptyProductState = () => {
    const products = document.getElementById('gf-products');
    if (!products) return;

    const hasProductCards = Boolean(
      products.querySelector('.card-wrapper.product-card-wrapper, .spf-product-card')
    );
    const isEmptyState = !hasProductCards && products.textContent.trim().length > 0;

    products.classList.toggle('ds-gf-products--empty', isEmptyState);
    Array.from(products.children).forEach((child) => {
      child.classList.toggle('ds-gf-empty-state', isEmptyState);
    });
  };

  const getBadgeKind = (text) => {
    const label = normalize(text);
    if (label === 'pre owned' || label === 'preowned') return 'preowned';
    if (label === 'sale') return 'sale';
    if (label === 'sold out' || label === 'soldout') return 'sold-out';
    if (label === 'new' || label === 'new arrival') return 'new';
    return '';
  };

  const applyBadgeClass = (badge, kind) => {
    if (!badge || !kind) return;

    badge.classList.add('ds-gf-card-badge', `ds-gf-card-badge--${kind}`);
    const originalBadgeClass = {
      preowned: 'preowned-pill',
      sale: 'sale-pill',
      'sold-out': 'sold-out-pill',
      new: 'new-pill',
    }[kind];

    if (originalBadgeClass) badge.classList.add(originalBadgeClass);
    if (kind === 'new' && badge.textContent !== 'NEW ARRIVAL') {
      badge.textContent = 'NEW ARRIVAL';
    }
    badge.dataset.dsBadgeKind = kind;
  };

  const resolveCardBadge = (card, imageArea) => {
    const badges = Array.from(imageArea.querySelectorAll('.ds-gf-card-badge'));
    const activeBadge =
      badges.find((badge) => badge.dataset.dsBadgeKind === 'sold-out') ||
      badges.find((badge) => badge.dataset.dsBadgeKind === 'preowned') ||
      badges.find((badge) => badge.dataset.dsBadgeKind === 'sale') ||
      badges.find((badge) => badge.dataset.dsBadgeKind === 'new');

    badges.forEach((badge) => {
      const isActive = badge === activeBadge;
      badge.hidden = !isActive;
      badge.setAttribute('aria-hidden', String(!isActive));
    });

    let rightBadgeLayer = imageArea.querySelector(
      ':scope > .ds-gf-product-pills-right'
    );
    const usesRightBadgeLayer = activeBadge?.dataset.dsBadgeKind === 'sold-out';

    if (usesRightBadgeLayer) {
      if (!rightBadgeLayer) {
        rightBadgeLayer = document.createElement('div');
        rightBadgeLayer.className = 'product-pills-right ds-gf-product-pills-right';
        imageArea.appendChild(rightBadgeLayer);
      }
      if (activeBadge.parentElement !== rightBadgeLayer) {
        rightBadgeLayer.appendChild(activeBadge);
      }
    } else if (activeBadge) {
      if (activeBadge.parentElement !== imageArea || imageArea.firstElementChild !== activeBadge) {
        imageArea.prepend(activeBadge);
      }
    }

    if (rightBadgeLayer && !rightBadgeLayer.children.length) {
      rightBadgeLayer.remove();
    }

    card.classList.toggle('ds-gf-has-card-badge', Boolean(activeBadge));
  };

  const loadNewBadge = (card, imageArea) => {
    if (card.dataset.dsNewBadgeState) return;

    const productJsonUrl = getProductJsonUrl(card);
    if (!productJsonUrl) {
      card.dataset.dsNewBadgeState = 'unavailable';
      return;
    }

    card.dataset.dsNewBadgeState = 'loading';
    fetchProduct(productJsonUrl)
      .then((product) => {
        card.dataset.dsNewBadgeState = 'ready';

        const createdAt = Date.parse(product.created_at || '');
        const productAge = Date.now() - createdAt;
        const newProductWindow = 30 * 24 * 60 * 60 * 1000;
        const isNewProduct =
          Number.isFinite(createdAt) &&
          productAge >= 0 &&
          productAge <= newProductWindow;

        if (
          isNewProduct &&
          !imageArea.querySelector('.ds-gf-card-badge--new')
        ) {
          const badge = document.createElement('span');
          badge.textContent = 'NEW ARRIVAL';
          applyBadgeClass(badge, 'new');
          imageArea.prepend(badge);
        }

        resolveCardBadge(card, imageArea);
      })
      .catch(() => {
        card.dataset.dsNewBadgeState = 'error';
        productRequests.delete(productJsonUrl);
      });
  };

  const enhanceProductBadges = () => {
    const isPreownedCollection = /\/collections\/(?:pre-owned|preowned)(?:\/|$)/i.test(
      window.location.pathname
    );

    document.querySelectorAll(PRODUCT_CARD_SELECTOR).forEach((card) => {
      const imageArea = card.querySelector('.card__inner');
      if (!imageArea) return;

      imageArea.querySelectorAll('span, div').forEach((candidate) => {
        const kind = getBadgeKind(candidate.textContent);
        if (!kind) return;

        const childHasSameLabel = Array.from(candidate.children).some(
          (child) => getBadgeKind(child.textContent) === kind
        );
        if (childHasSameLabel) return;

        const labelledAncestor = candidate.closest(
          '.preowned-pill, .new-pill, .sale-pill, .sold-out-pill, [class*="badge"], [class*="label"]'
        );
        const badge =
          labelledAncestor &&
          imageArea.contains(labelledAncestor) &&
          getBadgeKind(labelledAncestor.textContent) === kind
            ? labelledAncestor
            : candidate;

        applyBadgeClass(badge, kind);
      });

      const productTitle = card.querySelector('.card__heading')?.textContent || '';
      const isPreownedProduct =
        isPreownedCollection || /pre[\s-]?owned/i.test(productTitle);
      const hasPreownedBadge = imageArea.querySelector(
        '.ds-gf-card-badge--preowned'
      );

      if (isPreownedProduct && !hasPreownedBadge) {
        const badge = document.createElement('span');
        badge.textContent = 'PRE-OWNED';
        applyBadgeClass(badge, 'preowned');
        imageArea.prepend(badge);
      }

      resolveCardBadge(card, imageArea);
      loadNewBadge(card, imageArea);
    });
  };

  const getProductLink = (card) =>
    card?.querySelector(
      '.card__heading a[href*="/products/"], .card__media-link[href*="/products/"]'
    );

  const getProductJsonUrl = (card) => {
    const productLink = getProductLink(card);

    if (!productLink) return '';

    const url = new URL(productLink.href, window.location.origin);
    const productPath = url.pathname.split('/products/')[1] || '';
    const handle = productPath.split('/')[0];
    if (!handle) return '';

    const rootUrl = String(window.routes?.root_url || '/').replace(/\/$/, '');
    url.pathname = `${rootUrl}/products/${handle.replace(/\.js$/, '')}.js`;
    url.search = '';
    url.hash = '';
    return url.toString();
  };

  const fetchProduct = (url) => {
    if (!productRequests.has(url)) {
      productRequests.set(
        url,
        fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then(async (response) => {
          if (!response.ok) throw new Error('Product options could not be loaded.');
          return response.json();
        })
      );
    }

    return productRequests.get(url);
  };

  const syncQuickAddLayer = (wrapper) => {
    const card = wrapper.closest('.card-wrapper.product-card-wrapper');
    if (!card) return;

    const isActive =
      wrapper.matches(':hover') ||
      wrapper.classList.contains('is-open') ||
      wrapper.contains(document.activeElement);
    const gridItem = getGridItem(card);

    card.classList.toggle('ds-gf-quick-add-active', isActive);
    gridItem?.classList.toggle('ds-gf-quick-add-active', isActive);
  };

  const syncFilterDrawerState = () => {
    const tree = document.getElementById('gf-tree');
    if (!tree) return;

    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    const isOpen =
      !isDesktop &&
      (document.documentElement.classList.contains('offcanvas-open') ||
        document.body.classList.contains('offcanvas-open'));

    tree.classList.toggle('ds-gf-drawer-open', isOpen);

    if (isDesktop || isOpen) {
      tree.removeAttribute('aria-hidden');
    } else {
      tree.setAttribute('aria-hidden', 'true');
    }
  };

  const moveTreeForBreakpoint = () => {
    const tree = document.getElementById('gf-tree');
    const grid = document.getElementById('gf-grid');
    if (!tree || !grid) return;

    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      if (tree.parentElement !== document.body) {
        treePlaceholder = document.createComment('ds-globo-filter-tree');
        tree.parentNode.insertBefore(treePlaceholder, tree);
        document.body.appendChild(tree);
      }

      tree.setAttribute('role', 'dialog');
      tree.setAttribute('aria-modal', 'true');
      syncFilterDrawerState();
      return;
    }

    document.documentElement.classList.remove('offcanvas-open');
    document.body.classList.remove('offcanvas-open');
    document.getElementById('gf-overlay')?.remove();

    if (tree.parentElement === document.body) {
      if (treePlaceholder?.isConnected) {
        treePlaceholder.parentNode.insertBefore(tree, treePlaceholder);
        treePlaceholder.remove();
      } else if (grid.parentNode) {
        grid.parentNode.insertBefore(tree, grid);
      }
    }

    treePlaceholder = null;
    tree.removeAttribute('role');
    tree.removeAttribute('aria-modal');
    tree.removeAttribute('aria-hidden');
    tree.classList.remove('ds-gf-drawer-open');
  };

  const applyCompatibility = () => {
    labelFilterBlocks();
    hideSelectedFilterSummary();
    positionResetControl();
    labelFilterFooter();
    enhanceProductBadges();
    enhanceEmptyProductState();
    moveTreeForBreakpoint();
  };

  const scheduleCompatibility = () => {
    if (renderFrame) cancelAnimationFrame(renderFrame);
    renderFrame = requestAnimationFrame(() => {
      renderFrame = null;
      applyCompatibility();
    });
  };

  const scheduleBreakpointUpdate = () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      moveTreeForBreakpoint();
      document
        .querySelectorAll('.ds-gf-quick-add-wrapper')
        .forEach((wrapper) => syncQuickAddLayer(wrapper));
    });
  };

  const observeAppRenders = () => {
    if (compatibilityObserver || !document.body) return;

    compatibilityObserver = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => {
        const target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;

        /* Re-run filter and badge presentation when Globo renders new results. */
        if (target?.closest('#gf-products')) return true;

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return (
            node.matches('#gf-tree, #gf-grid, #gf-products, .card-wrapper.product-card-wrapper') ||
            Boolean(
              node.querySelector(
                '#gf-tree, #gf-grid, #gf-products, .card-wrapper.product-card-wrapper'
              )
            )
          );
        });
      });

      if (hasRelevantChange) scheduleCompatibility();
    });

    /* The app can replace the body during its delayed mobile price render. */
    compatibilityObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const observeDrawerState = () => {
    if (drawerStateObserver || !document.body) return;

    drawerStateObserver = new MutationObserver(syncFilterDrawerState);
    drawerStateObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    drawerStateObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  window.addEventListener('globoFilterRenderCompleted', scheduleCompatibility);
  window.addEventListener('globoFilterTreeRenderCompleted', scheduleCompatibility);
  window.addEventListener('resize', scheduleBreakpointUpdate, { passive: true });
  window.addEventListener('pageshow', scheduleCompatibility);
  document.addEventListener('shopify:section:load', scheduleCompatibility);
  document.addEventListener('pointerup', handleDelegatedResetPointerUp, true);
  document.addEventListener('click', handleDelegatedResetClick, true);
  document.addEventListener('click', () => {
    requestAnimationFrame(() => {
      syncFilterDrawerState();
      document
        .querySelectorAll('.ds-gf-quick-add-wrapper')
        .forEach((wrapper) => syncQuickAddLayer(wrapper));
    });
  });
  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Enter' &&
      !event.isComposing &&
      event.target instanceof Element &&
      event.target.matches(COLLECTION_SEARCH_SELECTOR)
    ) {
      /* Results already update on input. Treat the keyboard Search action as
         "done" so mobile browsers close the keyboard without submitting. */
      event.preventDefault();
      event.target.blur();
      return;
    }

    if (event.key !== 'Escape') return;
    requestAnimationFrame(() => {
      document
        .querySelectorAll('.ds-gf-quick-add-wrapper')
        .forEach((wrapper) => syncQuickAddLayer(wrapper));
    });
  });
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const input = form.querySelector(COLLECTION_SEARCH_SELECTOR);
      if (!input) return;

      /* This fallback covers mobile keyboards that emit submit without a
         detectable Enter keydown. Live filtering makes navigation unnecessary. */
      event.preventDefault();
      input.blur();
    },
    true
  );

  const initialize = () => {
    observeAppRenders();
    observeDrawerState();
    applyCompatibility();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
